import React, { useState, useEffect } from "react";
import "../../App.css";
import { useApp } from '../../core/context';
import { uuid } from '../../core/helpers';
import { sb } from '../../core/db';
import { InlineIcon } from '../../components/Icon';

const RBAC_MODULES = [
  ["clients",     "Clients"],
  ["invoices",    "Invoices"],
  ["payments",    "Payments"],
  ["tasks",       "Tasks"],
  ["recurring_tasks","Recurring Tasks"],
  ["tickets",     "Tickets"],
  ["marketplace", "Marketplace"],
  ["team_reports","Team & Reports"],
];
const RBAC_ACTIONS = ["view","create","edit","delete"];

function UserSettings() {
  const { showToast } = useApp();
  const [roles, setRoles]     = useState([]);
  const [users, setUsers]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [listFilter, setListFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [newName, setNewName] = useState("");
  const [newScope, setNewScope] = useState("own");

  const load = async () => {
    setLoading(true);
    try {
      const [usersData, rolesData] = await Promise.all([
        sb.listUsers(),
        sb.listRoles(),
      ]);
      setUsers((usersData || []).filter(u => u.role !== "deleted" && !u.is_deleted));
      setRoles((rolesData || []).sort((a,b)=>(b.is_system?1:0)-(a.is_system?1:0)));
    } catch (error) {
      showToast(error.message || "Could not load users and roles", "error");
    }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const addRole = async () => {
    if (!newName.trim()) { showToast("Enter a role name","error"); return; }
    const perms = {};
    RBAC_MODULES.forEach(([k]) => { perms[k] = { view:false, create:false, edit:false, delete:false }; });
    try {
      await sb.createRole({
        id: uuid(),
        name: newName.trim(),
        data_scope: newScope,
        permissions: perms,
        is_system: false,
      });
      showToast("Role created","success");
      setNewName("");
      setNewScope("own");
      load();
    } catch {
      showToast("Could not create role","error");
    }
  };

  const togglePerm = (roleId, mod, action) =>
    setRoles(rs => rs.map(r => {
      if (r.id!==roleId) return r;
      const cur = (r.permissions && r.permissions[mod]) || {};
      return { ...r, permissions: { ...r.permissions, [mod]: { ...cur, [action]: !cur[action] } } };
    }));

  const setScope = (roleId, val) =>
    setRoles(rs => rs.map(r => r.id===roleId ? { ...r, data_scope:val } : r));

  const saveRole = async (role) => {
    try {
      await sb.updateRole(role.id, {
        name: role.name,
        data_scope: role.data_scope,
        permissions: role.permissions,
      });
      showToast(`${role.name} saved`,"success");
    } catch {
      showToast("Save failed","error");
    }
  };

  const deleteRole = async (role) => {
    if (role.is_system) { showToast("Base roles cannot be deleted","error"); return; }
    if (!window.confirm(`Delete role "${role.name}"? This cannot be undone.`)) return;
    try {
      await sb.deleteRole(role.id);
      showToast("Role deleted","success");
      load();
    } catch {
      showToast("Delete failed","error");
    }
  };

  const query = search.trim().toLowerCase();
  const visibleUsers = users.filter(user =>
    !query || [user.name, user.email, user.phone, user.role].some(value => String(value || "").toLowerCase().includes(query))
  );
  const visibleRoles = roles.filter(role =>
    !query || [role.name, role.data_scope].some(value => String(value || "").toLowerCase().includes(query))
  );

  return (
    <div style={{ maxWidth: 1000 }}>
      <div className="card" style={{ marginBottom:18 }}>
        <div className="card-head"><div className="card-title">Create Role</div></div>
        <div className="card-body">
          <div style={{ fontSize:12, color:"var(--muted)", marginBottom:12 }}>Name a role, choose whether it sees all records or only its own, then tick the View / Create / Edit / Delete access per module below.</div>
          <div style={{ display:"flex", gap:12, alignItems:"flex-end", flexWrap:"wrap" }}>
            <div className="f-group" style={{ flex:1, minWidth:180 }}>
              <label className="f-label">Role name</label>
              <input className="f-input" placeholder="e.g. Telecaller" value={newName} onChange={e=>setNewName(e.target.value)} />
            </div>
            <div className="f-group" style={{ minWidth:210 }}>
              <label className="f-label">Data scope</label>
              <select className="f-select" value={newScope} onChange={e=>setNewScope(e.target.value)}>
                <option value="own">Sees only own records</option>
                <option value="all">Sees all records</option>
              </select>
            </div>
            <button className="btn btn-primary" onClick={addRole}>+ Add Role</button>
          </div>
        </div>
      </div>

      <div className="stat-cards cols-2" style={{ marginBottom:18 }}>
        <div className="stat-box" style={{ cursor:"default" }}>
          <div className="stat-lbl">Total Users</div>
          <div className="stat-val" style={{ color:"var(--blue)" }}>{users.length}</div>
          <div className="stat-note">Active user accounts</div>
        </div>
        <div className="stat-box" style={{ cursor:"default" }}>
          <div className="stat-lbl">Total Roles</div>
          <div className="stat-val" style={{ color:"var(--green)" }}>{roles.length}</div>
          <div className="stat-note">System and custom roles</div>
        </div>
      </div>

      <div className="card" style={{ marginBottom:18 }}>
        <div className="card-body users-roles-filter">
          <div className="f-group">
            <label className="f-label">Show</label>
            <select className="f-select" value={listFilter} onChange={e=>setListFilter(e.target.value)}>
              <option value="all">Users and roles</option>
              <option value="users">Users only</option>
              <option value="roles">Roles only</option>
            </select>
          </div>
          <div className="f-group users-roles-search">
            <label className="f-label">Search</label>
            <input className="f-input" value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search name, email, phone or role" />
          </div>
        </div>
      </div>

      {!loading && listFilter !== "roles" && (
        <div className="card" style={{ marginBottom:16 }}>
          <div className="card-head"><div className="card-title">Users ({visibleUsers.length})</div></div>
          {visibleUsers.length ? (
            <div className="tbl-wrap">
              <table>
                <thead><tr><th>Name</th><th>Email / Phone</th><th>Role</th><th>Status</th></tr></thead>
                <tbody>{visibleUsers.map(user => (
                  <tr key={user.id}>
                    <td style={{ fontWeight:600 }}>{user.name || "Unnamed user"}</td>
                    <td><div>{user.email || "—"}</div><div style={{ fontSize:11,color:"var(--muted)" }}>{user.phone || ""}</div></td>
                    <td style={{ textTransform:"capitalize" }}>{user.role || "—"}</td>
                    <td><span className="badge" style={{ background:"#DCFCE7",color:"#15803D" }}>Active</span></td>
                  </tr>
                ))}</tbody>
              </table>
            </div>
          ) : <div className="empty"><div>No users match this filter.</div></div>}
        </div>
      )}

      {loading ? <div className="empty"><div>Loading users and roles…</div></div> : listFilter === "users" ? null :
        visibleRoles.map(role => (
          <div key={role.id} className="card" style={{ marginBottom:16 }}>
            <div className="card-head" style={{ display:"flex", alignItems:"center", gap:10, flexWrap:"wrap" }}>
              <div className="card-title">{role.name}</div>
              {role.is_system && <span className="badge" style={{ background:"#EDE9FE", color:"#7C3AED", fontSize:10 }}>SYSTEM</span>}
              <div style={{ marginLeft:"auto", display:"flex", alignItems:"center", gap:10 }}>
                <span style={{ fontSize:12, color:"var(--muted)" }}>Data scope</span>
                <select className="f-select" style={{ width:210 }} value={role.data_scope} onChange={e=>setScope(role.id, e.target.value)}>
                  <option value="own">Sees only own records</option>
                  <option value="all">Sees all records</option>
                </select>
              </div>
            </div>
            <div className="card-body" style={{ overflowX:"auto" }}>
              <table style={{ width:"100%", fontSize:13, borderCollapse:"collapse" }}>
                <thead>
                  <tr style={{ textAlign:"left", color:"var(--muted)", fontSize:11, textTransform:"uppercase" }}>
                    <th style={{ padding:"8px 4px" }}>Module</th>
                    {RBAC_ACTIONS.map(a => <th key={a} style={{ padding:"8px 4px", textAlign:"center" }}>{a}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {RBAC_MODULES.map(([k,label]) => (
                    <tr key={k} style={{ borderTop:"1px solid #F3F4F6" }}>
                      <td style={{ padding:"8px 4px", fontWeight:600 }}>{label}</td>
                      {RBAC_ACTIONS.map(a => (
                        <td key={a} style={{ padding:"8px 4px", textAlign:"center" }}>
                          <input type="checkbox" checked={!!(role.permissions && role.permissions[k] && role.permissions[k][a])} onChange={()=>togglePerm(role.id, k, a)} />
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="modal-foot" style={{ display:"flex", gap:8 }}>
              {!role.is_system && <button className="btn btn-sm" style={{ background:"#FEF2F2", color:"var(--red)", border:"1px solid #FCA5A5", marginRight:"auto" }} onClick={()=>deleteRole(role)}><InlineIcon name="trash" size={13} gap={4}>Delete Role</InlineIcon></button>}
              <button className="btn btn-primary btn-sm" style={{ marginLeft:"auto" }} onClick={()=>saveRole(role)}>Save “{role.name}”</button>
            </div>
          </div>
        ))
      }
    </div>
  );
}

export { UserSettings };
